import React, { useState } from 'react'
import Input from 'components/input'
import Modal, { ModalProps } from 'components/modal'
import { validateSingleLinkField } from 'components/spreadsheet/helpers/functions'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import { Delete } from 'components/icons'
import Spinner from 'components/spinner'

interface MultilinkCellModalProps extends ModalProps {
  links: string[]
  isAdmin: boolean
  isContributor: boolean
  setCellValue: (value: string[], onSuccess: () => void, onError: () => void) => void
}

const MultilinkCellModal: React.FC<MultilinkCellModalProps> = ({
  id,
  open,
  setOpen,
  links,
  setCellValue,
  isAdmin,
  isContributor
}) => {
  const { setSnackbarMessage } = useApplicationStore()
  const [processing, setProcessing] = useState<boolean>(false)
  const [newLink, setNewLink] = useState<string>('')

  const handleAddLink = () => {
    if (processing || !newLink) return
    const result = validateSingleLinkField(newLink)
    setProcessing(true)
    if (result.pass) {
      setCellValue(
        [...links, newLink],
        () => setProcessing(false),
        () => setProcessing(false)
      )
    } else {
      setSnackbarMessage({ status: 'error', message: result.error })
      setProcessing(false)
    }
    setNewLink('')
  }

  const handleRemoveLink = (index: number) => {
    setProcessing(true)
    const newLinks = [...links]
    newLinks.splice(index, 1)
    setCellValue(
      newLinks,
      () => setProcessing(false),
      () => setProcessing(false)
    )
    setNewLink('')
  }

  const canAddLinks = isAdmin || isContributor

  return (
    <Modal id={id} open={open} setOpen={setOpen} title={'Edit Multilink'}>
      {canAddLinks && (
        <div className="inline-flex ml-[5px] mb-2.5">
          <Input
            placeholder="New link"
            value={newLink}
            disabled={processing}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewLink(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
          <Button
            internalType="accept"
            className="ml-2.5 w-[100px]"
            onClick={() => handleAddLink()}
            disabled={!newLink}
          >
            {processing ? <Spinner size="small" /> : 'Add'}
          </Button>
        </div>
      )}

      <ul className="w-full overflow-x-hidden h-[350px] my-0 mr-0 ml-2.5 list-none">
        {links.map((link, index) => (
          <li key={`${index}-${link}`} className="w-full inline-flex mt-2.5 pr-[5px]">
            <a
              className="relative flex items-center truncate mr-[5px] w-[calc(100%_-_30px)]"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link}
            </a>
            {canAddLinks && (
              <span
                className="relative mr-[5px] top-0.5"
                style={{ cursor: processing ? 'auto' : 'pointer', color: processing ? 'disabled' : 'inherit' }}
                onClick={() => {
                  if (!processing) handleRemoveLink(index)
                }}
              >
                <Delete className="h-6 w-6" />
              </span>
            )}
          </li>
        ))}
      </ul>
    </Modal>
  )
}

export default MultilinkCellModal
